import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, Send, Eye, Info, Power, PowerOff, Plus, Trash2, EyeOff, Palette, ShieldCheck, Zap, Code, Blocks, GripVertical, FolderOpen, ChevronDown, Pencil } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmailBlockBuilder, { compileBlocksToHtml } from "@/components/admin/EmailBlockBuilder";
import useListDnd from "@/lib/useListDnd";

const KEY_LABELS = {
  activation: "تفعيل الحساب",
  password_reset: "إعادة تعيين كلمة المرور",
  welcome: "الترحيب",
  course_granted: "منح دورة",
  cart_abandonment_1: "تذكير — لم يكمل الشراء",
};
const SEEDED_KEYS = new Set(["activation", "password_reset", "welcome", "course_granted", "cart_abandonment_1"]);
// Templates fired automatically by system events — surfaced with a "أساسي" badge so
// the admin doesn't accidentally treat them like manual broadcasts.
// Truth table (matches /app/backend/routes/automated_flows.py FLOWS):
//   • activation      — يُرسل فور إتمام الشراء + عند إعادة الإرسال يدويًا (يحوي رابط التفعيل)
//   • password_reset  — يُرسل عند طلب استعادة كلمة المرور
//   • welcome         — اختياري: يُرسل فقط عند التسجيل المباشر من /register (لا يُستخدم في تدفق الشراء)
//   • course_granted  — اختياري: يُرسل عند منح دورة يدويًا
//   • cart_abandonment_1 — يُرسل عبر تسلسل السلة المهجورة (لا يحتاج حملة)
const AUTOMATIC_KEYS = new Map([
  ["activation", { note: "يُرسل تلقائيًا فور إتمام الشراء وعند إعادة الإرسال يدويًا (يحوي رابط التفعيل)", mandatory: true }],
  ["password_reset", { note: "يُرسل تلقائيًا عند طلب استعادة كلمة المرور", mandatory: true }],
  ["welcome", { note: "اختياري: يُرسل فقط عند التسجيل المباشر من صفحة /register (لا يُستخدم في تدفق الشراء)", mandatory: false }],
  ["course_granted", { note: "اختياري: يُرسل عند منح دورة يدويًا من صفحة الأعضاء", mandatory: false }],
  ["cart_abandonment_1", { note: "يُرسل عبر تسلسل السلة المهجورة (لا يحتاج حملة)", mandatory: false }],
]);

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", body_html: "", description: "", design_theme: "default_cream", folder: "" });
  const [blocks, setBlocks] = useState([]);
  // Editor mode: 'blocks' (drag & drop) or 'html' (raw HTML). Auto-picked based on whether the
  // loaded template has a blocks_json payload.
  const [editorMode, setEditorMode] = useState("blocks");
  const [busy, setBusy] = useState(false);
  const [vars, setVars] = useState([]);
  const [themes, setThemes] = useState([]);
  const [showPreview, setShowPreview] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ key: "", subject: "", body_html: "", description: "", design_theme: "default_cream", folder: "" });
  const [folders, setFolders] = useState([]);  // [{name, count}]
  const [collapsedFolders, setCollapsedFolders] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("admin_email_folders_collapsed") || "[]")); }
    catch { return new Set(); }
  });

  const load = async () => {
    const { data } = await api.get("/admin/emails/templates");
    setTemplates(data);
    if (!selectedKey && data.length > 0) selectKey(data[0].key, data);
    // Refresh folder counts
    api.get("/admin/emails/folders").then((r) => setFolders(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/admin/emails/available-variables").then((r) => {
      setVars(r.data.variables || []);
      setThemes(r.data.design_themes || []);
    });
  }, []);

  const selectKey = (k, list = templates) => {
    setSelectedKey(k);
    const t = list.find((x) => x.key === k);
    if (t) {
      setForm({ name: t.name || "", subject: t.subject, body_html: t.body_html, description: t.description || "", design_theme: t.design_theme || "default_cream", folder: t.folder || "" });
      // Try to hydrate the block builder from the persisted blocks_json.
      let hydrated = [];
      if (t.blocks_json) {
        try { hydrated = JSON.parse(t.blocks_json) || []; } catch { hydrated = []; }
      }
      setBlocks(hydrated);
      setEditorMode(hydrated.length > 0 ? "blocks" : "html");
    }
  };

  const save = async () => {
    if (!selectedKey) return;
    setBusy(true);
    try {
      const payload = { ...form };
      // In blocks mode, compile blocks → HTML and persist both.
      if (editorMode === "blocks" && blocks.length > 0) {
        payload.body_html = compileBlocksToHtml(blocks);
        payload.blocks_json = JSON.stringify(blocks);
      } else if (editorMode === "blocks" && blocks.length === 0) {
        payload.blocks_json = null;  // no blocks — fall back to whatever body_html was typed
      } else {
        payload.blocks_json = null;  // HTML mode explicitly clears blocks_json
      }
      await api.put(`/admin/emails/templates/${selectedKey}`, payload);
      toast.success("تم حفظ القالب");
      await load();
    } catch (e) { toast.error(extractError(e, "فشل الحفظ")); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("أدخل بريد الاختبار"); return; }
    if (!selectedKey) return;
    setBusy(true);
    try {
      await api.post(`/admin/emails/templates/${selectedKey}/send-test`, { email: testEmail });
      toast.success(`تم إرسال بريد تجريبي إلى ${testEmail}`);
    } catch (e) { toast.error(extractError(e, "فشل الإرسال")); }
    finally { setBusy(false); }
  };

  const toggleDisabled = async () => {
    const cur = templates.find((t) => t.key === selectedKey);
    const willDisable = !cur?.disabled;
    if (willDisable && !confirm("سيتم إيقاف إرسال هذه الرسالة تلقائيًا. متأكد؟")) return;
    try {
      await api.patch(`/admin/emails/templates/${selectedKey}/toggle-disabled`);
      toast.success(willDisable ? "تم إيقاف القالب" : "تم تفعيل القالب");
      await load();
    } catch (e) { toast.error(extractError(e, "فشل التبديل")); }
  };

  const remove = async () => {
    if (!selectedKey) return;
    const isDefault = SEEDED_KEYS.has(selectedKey);
    const msg = isDefault
      ? "هذا قالب افتراضي — سيُعاد إنشاؤه تلقائيًا عند إعادة تشغيل الخادم. لإيقافه بشكل دائم استخدم زر (إيقاف). هل تريد الحذف رغم ذلك؟"
      : "سيتم حذف هذا القالب نهائيًا. متأكد؟";
    if (!confirm(msg)) return;
    try {
      await api.delete(`/admin/emails/templates/${selectedKey}`);
      toast.success("تم الحذف");
      setSelectedKey(null);
      await load();
    } catch (e) { toast.error(extractError(e, "فشل الحذف")); }
  };

  const createNew = async () => {
    if (!newTpl.key || !newTpl.subject) { toast.error("المُعرِّف والعنوان مطلوبان"); return; }
    setBusy(true);
    try {
      await api.post("/admin/emails/templates", newTpl);
      toast.success("تم إنشاء القالب");
      setCreateOpen(false);
      setSelectedKey(newTpl.key);
      setNewTpl({ key: "", subject: "", body_html: "", description: "" });
      await load();
    } catch (e) { toast.error(extractError(e, "فشل الإنشاء")); }
    finally { setBusy(false); }
  };

  const insertVar = (key) => {
    if (editorMode === "blocks") {
      // Copy to clipboard so the user can paste inside a block's text field.
      const tok = `{{${key}}}`;
      try { navigator.clipboard.writeText(tok); toast.success(`نُسخ ${tok} — الصقه داخل نص أي عنصر`); }
      catch { toast.info(`أضف ${tok} داخل نص أي عنصر`); }
      return;
    }
    setForm((f) => ({ ...f, body_html: f.body_html + `{{${key}}}` }));
  };

  // Live-interpolated preview (client-side approximation, replaces {{key}} with placeholders)
  const previewHtml = () => {
    const sample = { full_name: "أحمد", email: "user@example.com",
      activation_link: "https://example.com/activate?token=abc",
      reset_link: "https://example.com/reset?token=abc",
      checkout_link: "https://example.com/checkout",
      product_name: "برنامج التدريب", course_title: "الدورة التجريبية" };
    // In blocks mode, compile the current block state so preview is live.
    const source = editorMode === "blocks" && blocks.length > 0
      ? compileBlocksToHtml(blocks)
      : (form.body_html || "");
    return source.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => sample[k] ?? `{{${k}}}`);
  };

  const cur = templates.find((t) => t.key === selectedKey);

  // Drag-and-drop reordering for the template sidebar list.
  const persistOrder = async (newList, meta) => {
    const movedItem = meta?.movedItem;
    const targetItem = meta?.targetItem;
    // If the drop crossed folders, change the moved item's folder to match the target's folder.
    if (movedItem && targetItem && (movedItem.folder || "") !== (targetItem.folder || "")) {
      const newFolder = targetItem.folder || "";
      newList = newList.map((t) => t.key === movedItem.key ? { ...t, folder: newFolder } : t);
    }
    setTemplates(newList);  // optimistic
    try {
      // If folder changed, persist that FIRST so subsequent GETs reflect the folder change.
      if (movedItem && targetItem && (movedItem.folder || "") !== (targetItem.folder || "")) {
        await api.put("/admin/emails/templates/move-to-folder", {
          keys: [movedItem.key],
          folder: targetItem.folder || "",
        });
      }
      await api.put("/admin/emails/templates/reorder", { keys: newList.map((t) => t.key) });
    } catch (e) {
      toast.error(extractError(e, "فشل حفظ الترتيب"));
      await load();  // revert to server truth
    }
  };
  const { getItemProps } = useListDnd({
    items: templates,
    getId: (t) => t.key,
    onReorder: persistOrder,
  });

  // Drop onto a folder header → move item into that folder (empty target)
  const folderDropProps = (folderName) => ({
    onDragOver: (e) => { e.preventDefault(); },
    onDrop: async (e) => {
      e.preventDefault();
      const key = e.dataTransfer.getData("text/plain");
      if (!key) return;
      const item = templates.find((t) => t.key === key);
      if (!item || (item.folder || "") === folderName) return;
      // Optimistic: update folder locally, then persist
      const next = templates.map((t) => t.key === key ? { ...t, folder: folderName } : t);
      setTemplates(next);
      try {
        await api.put("/admin/emails/templates/move-to-folder", { keys: [key], folder: folderName });
        toast.success(folderName ? `نُقل إلى: ${folderName}` : "نُقل إلى: غير مصنّف");
        await load();
      } catch (err) { toast.error(extractError(err, "فشل النقل")); await load(); }
    },
  });

  // Group templates by folder for rendering. Uncategorized bucket key: "".
  const grouped = templates.reduce((acc, t) => {
    const f = t.folder || "";
    (acc[f] = acc[f] || []).push(t);
    return acc;
  }, {});
  // Folder display order: use the /folders endpoint order (alphabetical, uncategorized last).
  const folderOrder = folders.length > 0
    ? folders.map((f) => f.name)
    : [...new Set(templates.map((t) => t.folder || ""))].sort((a, b) => (a === "") - (b === "") || a.localeCompare(b));

  const toggleFolder = (name) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem("admin_email_folders_collapsed", JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const moveToFolder = async (key, folder) => {
    try {
      await api.put("/admin/emails/templates/move-to-folder", { keys: [key], folder });
      toast.success(folder ? `نُقل إلى: ${folder}` : "نُقل إلى: غير مصنّف");
      await load();
    } catch (e) { toast.error(extractError(e, "فشل النقل")); }
  };

  const renameFolder = async (oldName) => {
    const suggested = oldName || "مجلد جديد";
    const newName = window.prompt("اسم المجلد الجديد (اتركه فارغًا لإفراغ المجلد)", suggested);
    if (newName === null) return;  // cancelled
    if (newName.trim() === oldName) return;  // no change
    try {
      await api.put("/admin/emails/folders/rename", { old_name: oldName, new_name: newName.trim() });
      toast.success("تم تحديث المجلد");
      await load();
    } catch (e) { toast.error(extractError(e, "فشل إعادة التسمية")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-emails-templates">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">قوالب البريد</h1>
          <p className="text-foreground/60 mt-2 text-sm">حرِّر النصوص، أنشئ قوالب جديدة، وشاهد المعاينة مباشرة.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-new-tpl"><Plus className="h-4 w-4 me-2" /> قالب جديد</Button>
      </div>

      {/* Info banner: which templates fire automatically */}
      <Card className="p-4 bg-amber-50 border-amber-200" data-testid="templates-auto-banner">
        <div className="flex items-start gap-2 text-sm text-amber-900">
          <Zap className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">قوالب تُرسل تلقائيًا</p>
            <p className="text-amber-900/80 text-xs mb-2">
              الشارات:
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black text-white text-[9px] mx-1"><ShieldCheck className="h-2.5 w-2.5" /> أساسي</span>
              = يُرسل تلقائيًا ومهم لتشغيل التدفق (مثل بريد التفعيل بعد الشراء).
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] mx-1">اختياري</span>
              = يُرسل تلقائيًا في حالات معينة قد لا تحدث في تدفق عملك (مثل الترحيب بمن يسجل مباشرة بدون شراء) — تعطيله آمن.
              لعرض كامل التدفقات التلقائية اذهب إلى
              <Link className="underline mx-1" to="/admin/automated-flows">التدفقات التلقائية</Link>.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-[240px_1fr] gap-4">
        {/* Sidebar list */}
        <Card className="p-2 space-y-1 h-fit overflow-hidden min-w-0">
          <div className="px-2 py-1 text-[10px] text-foreground/50 flex items-center gap-1" data-testid="tpl-list-hint">
            <GripVertical className="h-3 w-3" /> اسحب لإعادة الترتيب
          </div>
          {folderOrder.map((folderName) => {
            const items = grouped[folderName] || [];
            if (items.length === 0) return null;
            const isCollapsed = collapsedFolders.has(folderName);
            const label = folderName || "غير مصنّف";
            return (
              <div key={folderName || "__uncat__"} data-testid={`tpl-folder-${folderName || "uncat"}`}>
                <div
                  className="flex items-center gap-1 px-1 py-1 group rounded transition-colors [&.folder-dropzone-active]:bg-primary/15"
                  {...folderDropProps(folderName)}
                  onDragEnter={(e) => e.currentTarget.classList.add("folder-dropzone-active")}
                  onDragLeave={(e) => e.currentTarget.classList.remove("folder-dropzone-active")}
                  onDropCapture={(e) => e.currentTarget.classList.remove("folder-dropzone-active")}
                  data-testid={`tpl-folder-dropzone-${folderName || "uncat"}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleFolder(folderName)}
                    className="flex-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground/50 hover:text-foreground text-right"
                    data-testid={`tpl-folder-toggle-${folderName || "uncat"}`}
                  >
                    <FolderOpen className="h-3 w-3" />
                    <span className="truncate">{label}</span>
                    <span className="text-[9px] opacity-60">({items.length})</span>
                    <ChevronDown className={`h-3 w-3 me-auto transition-transform ${isCollapsed ? "-rotate-90 rtl:rotate-90" : ""}`} />
                  </button>
                  {folderName && (
                    <button
                      type="button"
                      onClick={() => renameFolder(folderName)}
                      className="opacity-0 group-hover:opacity-70 hover:!opacity-100 p-0.5 text-foreground/60"
                      title="إعادة تسمية"
                      data-testid={`tpl-folder-rename-${folderName}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {!isCollapsed && items.map((t) => (
                  <div
                    key={t.key}
                    {...getItemProps(t)}
                    className={`group flex items-stretch rounded-lg transition ms-1 min-w-0 ${
                      selectedKey === t.key ? "bg-black text-white" : "hover:bg-foreground/5"
                    } data-[dragging=true]:opacity-40 data-[dragover=true]:ring-2 data-[dragover=true]:ring-primary/60`}
                    data-testid={`tpl-row-${t.key}`}
                  >
                    <div
                      className={`flex items-center px-1 cursor-grab active:cursor-grabbing touch-none opacity-40 group-hover:opacity-80 shrink-0 ${selectedKey === t.key ? "text-white" : "text-foreground"}`}
                      title="اسحب لإعادة الترتيب"
                      data-testid={`tpl-drag-${t.key}`}
                    >
                      <GripVertical className="h-4 w-4" />
                    </div>
                    <button
                      onClick={() => selectKey(t.key)}
                      className="flex-1 min-w-0 text-right px-2 py-2 text-sm overflow-hidden"
                      data-testid={`tpl-${t.key}`}
                    >
                      <div className="font-medium flex items-center gap-2 min-w-0">
                        <span className="flex-1 min-w-0 truncate">{t.name || KEY_LABELS[t.key] || t.key}</span>
                        {AUTOMATIC_KEYS.has(t.key) && (
                          AUTOMATIC_KEYS.get(t.key).mandatory ? (
                            <span
                              title={AUTOMATIC_KEYS.get(t.key).note}
                              className={`text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${selectedKey === t.key ? "bg-white/20 text-white" : "bg-black text-white"}`}
                              data-testid={`tpl-badge-auto-${t.key}`}
                            >
                              <ShieldCheck className="h-2.5 w-2.5" /> أساسي
                            </span>
                          ) : (
                            <span
                              title={AUTOMATIC_KEYS.get(t.key).note}
                              className={`text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${selectedKey === t.key ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}
                              data-testid={`tpl-badge-auto-${t.key}`}
                            >
                              اختياري
                            </span>
                          )
                        )}
                        {t.disabled && <span className={`text-[9px] px-1.5 py-0.5 rounded ${selectedKey === t.key ? "bg-white/20 text-white" : "bg-red-100 text-red-700"}`}>معطّل</span>}
                      </div>
                      <div className={`text-xs truncate mt-0.5 ${selectedKey === t.key ? "opacity-70" : "opacity-60"}`}>{t.subject}</div>
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </Card>

        {/* Editor + Live Preview */}
        {selectedKey && cur ? (
          <Card className="p-4 sm:p-6 space-y-4">
            {/* Header */}
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-lg font-display font-semibold truncate">{cur.name || KEY_LABELS[selectedKey] || selectedKey}</h2>
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full bg-foreground/5 font-mono"
                  title="المُعرِّف الفني — لا يمكن تغييره. يستخدمه النظام في التسلسلات والجداول المُبرمجة."
                >
                  <span className="opacity-60 me-1">ID:</span>{selectedKey}
                </span>
                {AUTOMATIC_KEYS.has(selectedKey) && (
                  AUTOMATIC_KEYS.get(selectedKey).mandatory ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-black text-white flex items-center gap-1" data-testid="tpl-header-badge-auto">
                      <ShieldCheck className="h-3 w-3" /> يُرسل تلقائيًا — أساسي
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700" data-testid="tpl-header-badge-auto">
                      اختياري — قد لا يُستخدم
                    </span>
                  )
                )}
                {cur.disabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">معطّل — لا يُرسل</span>}
              </div>
              <div className="flex items-center gap-1">
                <Button variant={cur.disabled ? "default" : "outline"} size="sm" onClick={toggleDisabled} data-testid="btn-toggle-disabled">
                  {cur.disabled ? <><Power className="h-4 w-4 me-1" /> تفعيل</> : <><PowerOff className="h-4 w-4 me-1" /> إيقاف</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} data-testid="btn-toggle-preview">
                  {showPreview ? <><EyeOff className="h-4 w-4 me-1" /> إخفاء المعاينة</> : <><Eye className="h-4 w-4 me-1" /> إظهار المعاينة</>}
                </Button>
                <Button variant="ghost" size="sm" onClick={remove} data-testid="btn-delete-tpl"><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </div>

            {AUTOMATIC_KEYS.has(selectedKey) && (
              <div
                className={`rounded-lg border p-2.5 text-xs flex items-start gap-2 ${
                  AUTOMATIC_KEYS.get(selectedKey).mandatory
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-slate-50 border-slate-200 text-slate-700"
                }`}
                data-testid="tpl-auto-note"
              >
                <Zap className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div>
                  <strong>
                    {AUTOMATIC_KEYS.get(selectedKey).mandatory ? "قالب أساسي:" : "قالب اختياري:"}
                  </strong>{" "}
                  {AUTOMATIC_KEYS.get(selectedKey).note}. لإيقافه استخدم زر
                  <span className="mx-1 px-1.5 py-0.5 rounded bg-white/70 border">إيقاف</span>
                  أعلاه — لا حاجة لحذفه.
                </div>
              </div>
            )}

            {/* Editor + preview */}
            <div className={showPreview ? "grid xl:grid-cols-2 gap-4" : ""}>
              {/* Edit column */}
              <div className="space-y-3">
                <div>
                  <Label>الاسم الظاهر <span className="text-foreground/50 text-[10px]">— يظهر في القائمة الجانبية وعنوان القالب</span></Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={KEY_LABELS[selectedKey] || selectedKey}
                    data-testid="tpl-name"
                  />
                </div>
                <div>
                  <Label>وصف داخلي (لن يظهر للمستلم)</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tpl-desc" />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><FolderOpen className="h-3.5 w-3.5" /> المجلد</Label>
                  <div className="flex gap-1">
                    <Input
                      list="email-folder-list"
                      value={form.folder}
                      onChange={(e) => setForm({ ...form, folder: e.target.value })}
                      placeholder="مثال: مصادقة، تسويق، إشعارات..."
                      data-testid="tpl-folder"
                      className="flex-1"
                    />
                    <datalist id="email-folder-list">
                      {folders.map((f) => f.name && <option key={f.name} value={f.name} />)}
                    </datalist>
                  </div>
                  <p className="text-[10px] text-foreground/50 mt-1">اترك الحقل فارغًا لوضع القالب في "غير مصنّف". يظهر المجلد كمجموعة قابلة للطي في القائمة.</p>
                </div>
                <div>
                  <Label className="flex items-center gap-2"><Palette className="h-3.5 w-3.5" /> تصميم القالب</Label>
                  <Select value={form.design_theme} onValueChange={(v) => setForm({ ...form, design_theme: v })}>
                    <SelectTrigger data-testid="tpl-theme"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {themes.map((t) => <SelectItem key={t.key} value={t.key}>{t.desc}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-foreground/50 mt-1">للتحكم الكامل بالتصميم اختر "أبيض فارغ" واكتب HTML كامل بنفسك.</p>
                </div>
                <div>
                  <Label>عنوان البريد</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="tpl-subject" />
                </div>
                <div>
                  <div className="flex justify-between items-center flex-wrap gap-2 mb-2">
                    <div className="flex items-center gap-1 border rounded-md p-0.5 bg-muted/30" data-testid="editor-mode-toggle">
                      <button
                        type="button"
                        onClick={() => {
                          // Moving from html → blocks: if blocks are empty, warn that HTML will be preserved
                          setEditorMode("blocks");
                        }}
                        className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${editorMode === "blocks" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                        data-testid="editor-mode-blocks"
                      >
                        <Blocks className="h-3 w-3" /> عناصر (سحب وإفلات)
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorMode("html")}
                        className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${editorMode === "html" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                        data-testid="editor-mode-html"
                      >
                        <Code className="h-3 w-3" /> HTML
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {vars.map((v) => (
                        <button key={v.key} type="button" onClick={() => insertVar(v.key)} title={v.desc}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-foreground/5 hover:bg-foreground/10">{`{{${v.key}}}`}</button>
                      ))}
                    </div>
                  </div>

                  {editorMode === "blocks" ? (
                    <div className="space-y-2">
                      <EmailBlockBuilder blocks={blocks} onChange={setBlocks} />
                      {blocks.length === 0 && form.body_html && (
                        <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-[11px] text-blue-900" data-testid="blocks-empty-hint">
                          ℹ لا توجد عناصر بعد — سيُرسل البريد بمحتوى HTML الحالي كما هو. أضف عناصر لتحل محل ذلك عند الحفظ.
                        </div>
                      )}
                      <p className="text-[10px] text-foreground/50">
                        تلميح: اسحب أي عنصر من المقبض <span className="mx-1">⋮⋮</span> لإعادة ترتيبه.
                        استخدم زر <code className="bg-foreground/5 px-1 rounded" dir="ltr">{"{{"}var{"}}"}</code> لإضافة متغيرات إلى نص الفقرات.
                        عند الحفظ، تُحوّل العناصر تلقائيًا إلى HTML جاهز للإرسال.
                      </p>
                    </div>
                  ) : (
                    <>
                      <Label>محتوى الرسالة (HTML)</Label>
                      <Textarea rows={14} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                        className="font-mono text-sm [direction:ltr]" data-testid="tpl-body" />
                    </>
                  )}
                </div>
              </div>
              {/* Live preview column */}
              {showPreview && (
                <div className="space-y-2">
                  <Label>معاينة مباشرة</Label>
                  <div className="border rounded-xl bg-[#F9F5EE] overflow-hidden">
                    <div className="bg-foreground/5 px-3 py-2 text-xs border-b">
                      <span className="text-foreground/60">الموضوع:</span> <strong>{form.subject || "—"}</strong>
                    </div>
                    <div className="p-4 max-h-[520px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: previewHtml() }} />
                  </div>
                  <div className="rounded-lg bg-blue-50 border border-blue-200 p-2 text-[11px] text-blue-900 flex items-start gap-1.5">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <div>المعاينة تستخدم قيمًا تجريبية للمتغيرات. المتلقي سيرى بياناته الحقيقية.</div>
                  </div>
                </div>
              )}
            </div>

            {/* Save + test */}
            <div className="flex flex-wrap gap-2 pt-3 border-t">
              <Button onClick={save} disabled={busy} data-testid="btn-save-tpl">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 me-2" /> حفظ التغييرات</>}
              </Button>
              <div className="flex-1 min-w-[200px] flex gap-2">
                <Input placeholder="أرسل نسخة تجريبية إلى بريدك" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="tpl-test-email" />
                <Button variant="outline" onClick={sendTest} disabled={busy || !testEmail} data-testid="btn-send-test">
                  <Send className="h-4 w-4 me-2" /> اختبار
                </Button>
              </div>
            </div>
          </Card>
        ) : <Card className="p-8 text-center text-foreground/50">اختر قالبًا من القائمة أو أنشئ قالبًا جديدًا</Card>}
      </div>

      {/* Create new template dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader><DialogTitle>إنشاء قالب جديد</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>المُعرِّف (بالإنجليزية، مثل promo_offer)</Label>
              <Input value={newTpl.key} onChange={(e) => setNewTpl({ ...newTpl, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                placeholder="promo_offer" className="[direction:ltr]" data-testid="new-tpl-key" />
              <p className="text-[10px] text-foreground/50 mt-1">أحرف إنجليزية صغيرة وأرقام وشرطات سفلية فقط. لا يمكن تغييره لاحقًا.</p>
            </div>
            <div>
              <Label>وصف داخلي</Label>
              <Input value={newTpl.description} onChange={(e) => setNewTpl({ ...newTpl, description: e.target.value })} placeholder="متى يُرسل هذا القالب؟" />
            </div>
            <div>
              <Label>عنوان البريد</Label>
              <Input value={newTpl.subject} onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })} placeholder="عرض خاص لك!" data-testid="new-tpl-subject" />
            </div>
            <div>
              <Label>محتوى (HTML)</Label>
              <Textarea rows={6} value={newTpl.body_html} onChange={(e) => setNewTpl({ ...newTpl, body_html: e.target.value })}
                placeholder="<p>مرحبًا {{full_name}}...</p>" className="[direction:ltr] font-mono text-sm" data-testid="new-tpl-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>إلغاء</Button>
            <Button onClick={createNew} disabled={busy} data-testid="btn-create-tpl-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "إنشاء"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
